<template>
  <div
    class="application-recommendation"
    :style="{ 'min-height': loading ? '400px' : '' }">
    <Loading v-if="loading" />

    <!-- main crops tabs -->
    <div
      v-if="showTabs"
      class="application-recommendation__tabs"
      :style="{ '--crops-count': getMainCrops.length }">
      <div
        v-for="crop in getMainCrops"
        :key="crop.id"
        ref="mainCropTabs"
        class="application-recommendation__tab"
        :class="{
          'application-recommendation__tab--active':
            crop.id === selectedMainCropId,
        }"
        @click="selectedMainCropId = crop.id">
        <div
          v-if="crop.crop_icon"
          class="application-recommendation__crop-icon">
          <picture class="responsive-image">
            <img
              :src="crop.crop_icon"
              :alt="`${crop.name} icon`"
              class="responsive_image__img" />
          </picture>
        </div>
        <div class="application-recommendation__crop-name">
          {{ crop.name }}
        </div>
      </div>
    </div>

    <!-- crops additional info / but show only active -->
    <Transition
      v-for="crop in getMainCrops"
      :key="crop.id"
      name="tabs-content"
      mode="out-in"
      appear>
      <div
        v-if="crop.id === selectedMainCropId"
        class="application-recommendation__recommendation">
        <!-- add webform class for <select> to get styling -->
        <div
          class="application-recommendation__info-top webform-submission-form">
          <!-- crop name -->
          <h3>{{ getCropNameById(selectedMainCropId) }}</h3>

          <!-- crop sub crops -->
          <div class="form-type-select-wrapper">
            <select
              v-model="selectedSubCrop"
              :disabled="selectDisabled"
              class="application-recommendation__select">
              <option v-if="hasGeneral(selectedMainCropId)" value="general">
                {{ t.general }}
              </option>
              <option
                v-for="subCrop in getCropsByParent"
                :key="subCrop.id"
                :value="subCrop.id">
                {{ subCrop.name }}
              </option>
            </select>
            <div class="select-additional"></div>
          </div>
        </div>

        <!-- crops information -->
        <Transition name="tabs-content" mode="out-in" appear>
          <!-- need for transition classes -->
          <div
            v-if="
              getRecommendationBySubCrop && getRecommendationBySubCrop.crop > 0
            "
            class="application-recommendation__info-bottom"
            :key="selectedSubCrop">
            <!-- Intended application -->
            <div v-if="getRecommendationBySubCrop.intendedApplication">
              <h4 class="application-recommendation__label">
                {{ t.intendedApplication }}
              </h4>
              <p
                class="application-recommendation__desc"
                v-html="getRecommendationBySubCrop.intendedApplication"></p>
            </div>

            <div v-if="getRecommendationBySubCrop.frequencyPerYear">
              <!-- Application frequency -->
              <h4 class="application-recommendation__label">
                {{ t.frequencyPerYear }}
              </h4>
              <p
                class="application-recommendation__desc"
                v-html="getRecommendationBySubCrop.frequencyPerYear"></p>
            </div>

            <div class="application-recommendation__details">
              <!-- Application rate -->
              <!-- @todo values for these can be empty, add if -->
              <h4 class="application-recommendation__label">
                {{ t.applicationRate }}
              </h4>

              <!-- Time of application -->
              <h4 class="application-recommendation__label">
                {{ t.timeOfApplication }}
              </h4>

              <div
                v-for="detail in getApplicationDetailsByRecommendation"
                :key="detail.id">
                <div class="application-recommendation__desc-wrapper">
                  <div
                    v-if="detail.applicationRate"
                    v-html="detail.applicationRate"
                    class="application-recommendation__desc"></div>
                  <i
                    v-if="detail.timeOfApplication"
                    class="fa-solid fa-arrow-right-long"></i>
                </div>
                <div
                  v-if="detail.timeOfApplication"
                  v-html="detail.timeOfApplication"
                  class="application-recommendation__desc"></div>
              </div>
            </div>

            <!-- crop detail page link -->
            <div
              v-if="getCropUrlById(selectedSubCrop)"
              class="application-recommendation__link">
              <a
                :href="getCropUrlById(selectedSubCrop)"
                class="button button--primary"
                target="_blank">
                {{ t.subCropLink }}
              </a>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script>
// components
import Loading from './elements/Loading';
// utils
import translationStrings from '../translations/translationStrings';
import { getApplicationRecommendations } from '../utils/api';

export default {
  name: 'application-recommendation',
  components: {
    Loading,
  },
  props: {
    productId: {
      required: true,
      type: Number,
    },
    lang: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      applicationDetails: [],
      crops: [],
      lastSelectedMainCrop: 0,
      lastSelectedSubCrop: 0,
      loading: true,
      newActiveIndex: 0,
      recommendation: [],
      selectedMainCropId: 0,
      selectedSubCrop: 0,
      t: translationStrings,
    };
  },
  computed: {
    getMainCrops() {
      return this.crops.filter((crop) => crop.parent === 0);
    },
    getCropsByParent() {
      return this.crops.filter(
        (crop) => crop.parent === this.selectedMainCropId,
      );
    },
    getRecommendationBySubCrop() {
      // if the main crop doesn't have any sub crops
      if (this.lastSelectedSubCrop === 'general') {
        return this.recommendation.find(
          (recommendation) => recommendation.crop === this.selectedMainCropId,
        );
      }
      // if main crop has sub crops
      return this.recommendation.find(
        (recommendation) => recommendation.crop === this.lastSelectedSubCrop,
      );
    },
    getApplicationDetailsByRecommendation() {
      if (this.getRecommendationBySubCrop) {
        return this.applicationDetails.filter(
          (detail) =>
            detail.recommendationId === this.getRecommendationBySubCrop.id,
        );
      }
      return '';
    },
    selectDisabled() {
      const hasSingleSubCrop = this.getCropsByParent.length === 1;
      const hasGeneral = this.hasGeneral(this.selectedMainCropId);
      const hasSubCrops = this.getCropsByParent.length >= 1;

      if (
        (hasSingleSubCrop && hasGeneral) ||
        (hasSubCrops && !hasSingleSubCrop)
      ) {
        return false;
      }

      return true;
    },
    showTabs() {
      return this.getMainCrops.length > 1;
    },
  },
  watch: {
    selectedMainCropId(newMainCrop, oldMainCrop) {
      if (!this.hasGeneral(newMainCrop)) {
        const firstSubOfMain = this.crops.find(
          (crop) => crop.parent === newMainCrop,
        );
        this.selectedSubCrop = firstSubOfMain ? firstSubOfMain.id : 0;
      } else {
        if (newMainCrop !== 0) {
          this.lastSelectedMainCrop = 0;
          setTimeout(() => {
            this.selectedSubCrop = 'general';
          }, 0);
        } else if (newMainCrop === 0) {
          this.lastSelectedMainCrop = oldMainCrop;
        }
        this.selectedSubCrop = newMainCrop;
      }

      // set transition classes for tab green line
      this.newActiveIndex = this.getMainCrops.findIndex(
        (i) => i.id === newMainCrop,
      );
      if (this.showTabs) {
        this.$nextTick(() => this.handleActiveLine());
      }
    },
    selectedSubCrop(newSubCrop, oldSubCrop) {
      if (newSubCrop !== 0) {
        this.lastSelectedSubCrop = 0;
        setTimeout(() => {
          this.lastSelectedSubCrop = newSubCrop;
        }, 400);
      } else if (newSubCrop === 0) {
        this.lastSelectedSubCrop = oldSubCrop;
      }
      this.selectedSubCrop = newSubCrop;
    },
  },
  mounted() {
    // get the data
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        const { data } = await getApplicationRecommendations(
          this.lang,
          this.productId,
        );
        this.crops = data.crops;
        this.recommendation = data.recommendation;
        this.applicationDetails = data.applicationDetails;
      } catch ({ response }) {
        if (response) {
          console.error(response);
        }
      } finally {
        this.selectedMainCropId = this.getMainCrops[0].id;
        this.selectedSubCrop = this.getCropsByParent;

        // disable loading
        this.loading = false;
      }
    },
    handleActiveLine() {
      const tabClass = 'application-recommendation__tab--to-';
      const mainTabs = this.$refs.mainCropTabs;

      if (mainTabs) {
        mainTabs.forEach((tab, idx) => {
          tab.classList.remove(`${tabClass}left`);
          tab.classList.remove(`${tabClass}right`);

          if (this.newActiveIndex === idx) {
            return;
          }

          if (this.newActiveIndex > idx) {
            tab.classList.add(`${tabClass}left`);
          } else {
            tab.classList.add(`${tabClass}right`);
          }
        });
      }
    },
    hasGeneral(cropId) {
      return (
        this.recommendation.filter((rec) => rec.crop === cropId).length > 0
      );
    },
    getCropNameById(cropId) {
      const crop = this.crops.find((item) => item.id === cropId);
      if (crop) {
        return crop.name;
      }
    },
    getCropUrlById(cropId) {
      // show main crop link if sub crop is 'General'
      if (this.selectedSubCrop === 'general') {
        cropId = this.selectedMainCropId;
      }

      const crop = this.crops.find((item) => item.id === cropId);
      return crop && crop.crop_url;
    },
  },
};
</script>
